import { Alert, Autocomplete, Box, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import * as integrationsApi from '@/api/integrations';
import type { ImportSource, IntegrationSchema } from '@/api/integrations';

import type { SchemaProp } from '../../config/schema-grid/types';
import { SchemaValueField } from '../../config/schema-grid/ValueCell';
import { SEND_DEFAULTS, type BookingWizardData } from './types';

interface Props {
  toId: number;
  booking: BookingWizardData;
  onBooking: (partial: Partial<BookingWizardData>) => void;
  schema: IntegrationSchema | undefined;
  schemaLoading: boolean;
}

const SEND_KEYS = Object.keys(SEND_DEFAULTS);

export function Step4BookingParams({ toId, booking, onBooking, schema, schemaLoading }: Props) {
  const [selected, setSelected] = useState<ImportSource | null>(null);

  // Preselect the default import key when none has been chosen yet.
  // Import sources are a small bounded set per TO — load them all so the
  // dropdown shows every option (and the preselected default) without the user
  // having to type. (RemoteAutocomplete only fetches on input, so it showed no
  // options here.)
  const sourcesQuery = useQuery({
    queryKey: ['integrations', 'importSources', toId],
    queryFn: () => integrationsApi.searchImportSources(toId, ''),
  });
  const sources = sourcesQuery.data ?? [];
  useEffect(() => {
    if (!sourcesQuery.data) return;
    if (booking.source_id == null && sourcesQuery.data.length > 0) {
      // Preselect the default import key when none has been chosen yet.
      const def = sourcesQuery.data.find((s) => s.is_default) ?? sourcesQuery.data[0];
      if (def) {
        setSelected(def);
        onBooking({ source_id: def.code });
      }
    } else if (booking.source_id != null && selected == null) {
      // Restore the selected object (e.g. when returning to this step).
      setSelected(sourcesQuery.data.find((s) => s.code === booking.source_id) ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourcesQuery.data]);

  const props = (schema?.properties ?? {}) as Record<string, SchemaProp>;
  // required ∪ x-auth-keys, minus source_id (its own control above).
  //
  // x-auth-keys is the set of things needed to actually talk to the system, and
  // it is not always a subset of `required`: prioticket's webhook secret is one
  // — an account that only pulls via the API needs none, so making it required
  // would warn on every such config, but an account that DOES receive webhooks
  // rejects every delivery without it. Asking here is the cheap moment.
  const authKeys = ((schema?.['x-auth-keys'] as string[] | undefined) ?? []).filter(
    (k) => k !== 'source_id',
  );
  const requiredAuth = [
    ...new Set([...(schema?.required ?? []).filter((k) => k !== 'source_id'), ...authKeys]),
  ];

  // Seed declared defaults so a required key that has one (prioticket's
  // webclient_environment: "production") arrives pre-filled instead of as an
  // empty control the operator must touch before Next will accept the step.
  // The config grid already pre-fills from prop.default; this matches it.
  useEffect(() => {
    if (!schema) return;
    const seed: Record<string, unknown> = {};
    for (const key of requiredAuth) {
      const def = props[key]?.default;
      if (def !== undefined && booking.config[key] === undefined) seed[key] = def;
    }
    if (Object.keys(seed).length > 0) onBooking({ config: { ...booking.config, ...seed } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema]);

  function setConfig(key: string, v: unknown) {
    onBooking({ config: { ...booking.config, [key]: v } });
  }

  if (schemaLoading) {
    return <Typography variant="body2">Loading configuration schema…</Typography>;
  }
  if (!schema) {
    return <Alert severity="error">Couldn’t load the configuration schema for this system.</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Autocomplete<ImportSource>
        options={sources}
        loading={sourcesQuery.isLoading}
        value={selected}
        getOptionLabel={(s) => s.name}
        isOptionEqualToValue={(a, b) => a.code === b.code}
        onChange={(_, v) => {
          setSelected(v);
          onBooking({ source_id: v ? v.code : null });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Import source (source_id)"
            helperText={selected ? `code: ${selected.code}` : 'Bookings import into this source.'}
          />
        )}
      />

      {requiredAuth.map((key) => {
        const prop = props[key];
        if (!prop) return null;
        return (
          <SchemaValueField
            key={key}
            prop={prop}
            label={key}
            value={booking.config[key]}
            onChange={(v) => setConfig(key, v)}
          />
        );
      })}

      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        Surveys &amp; invitations
      </Typography>
      {SEND_KEYS.map((key) => {
        const prop = props[key];
        return (
          <FormControlLabel
            key={key}
            control={
              <Switch
                checked={Boolean(booking.config[key])}
                onChange={(e) => setConfig(key, e.target.checked)}
              />
            }
            label={(prop?.description as string | undefined) ?? key}
          />
        );
      })}
    </Box>
  );
}
